import {MutableRefObject, useContext, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {Product} from '../../../../../../../backend/enums/product';
import {UserType} from '../../../../../../../backend/enums/user-type';
import {User} from '../../../../../../../backend/interfaces/user';
import {AffiliatedUser} from '../../../../../../../backend/responses/affiliated-user';
import {AlertModal} from '../../../../../../../components/alert-modal/alert-modal';
import {ContentPage} from '../../../../../../../components/content-page/content-page';
import {PageHeader} from '../../../../../../../components/page-header/page-header';
import {PaginatedTable} from '../../../../../../../components/paginated-table/paginated-table';
import {AppContext} from '../../../../../../../contexts/app/context';
import {AppContextType} from '../../../../../../../contexts/app/types';
import {SnackBarContext} from '../../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../../contexts/snack-bar/types';
import {ScreenPaths} from '../../../../../../../enums/screen-path';
import {usePaginatedTable} from '../../../../../../../hooks/paginated-table';
import {translations} from '../../../../../../../i18n/translations';
import {PaginatedTableData} from '../../../../../../../interfaces/paginated-table-data';
import {UsersRepository} from '../../../../../../../repositories/users';
import {getBrandName} from '../../../../../../../utils/functions/get-product-name';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  Gender = 'gender',
  State = 'state',
}

export default function EntitySeniorsSystemAdministratorOfficeScreen() {
  // Get params
  let {id}: {id?: string} = useParams();

  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const appContext: AppContextType = useContext(AppContext);

  // Hold state
  const [updateSeniorDialogOpened, setUpdateSeniorDialogOpened] = useState<{
    disableDialogOpened: boolean;
    enableDialogOpened: boolean;
  }>({disableDialogOpened: false, enableDialogOpened: false});

  // Hold ref
  const dataToUpdateRef: MutableRefObject<{senior: User; seniorActiveFlag: boolean} | undefined> =
    useRef();

  // Translations
  const {t} = useTranslation();

  // Fetch seniors
  const handleGetSeniors = async (limit: number, skip: number, search?: string) => {
    // Check entity id
    if (!id) {
      // Error
      throw new Error('Empty entity id.');
    }

    // Load seniors
    const result: {totalCount: number; data: Array<AffiliatedUser>} =
      await UsersRepository.instance.getInEntity(id, limit, skip, UserType.Senior, search);

    // Process data
    const processedData: PaginatedTableData<AffiliatedUser> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.user.id || '',
        item: obj,
        enabled: obj.user.isActive,
        avatarUrl: obj.user.picture,
        toLink: `${ScreenPaths.Office}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Seniors}/${obj.user.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            value: obj.user.name || '',
          },
          {
            columnId: ColumnIdentifier.Gender,
            value: obj.user.gender ? t(translations.genders[obj.user.gender]) : '',
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.user.isActive
              ? t(translations.entities.edit.seniors.state_value_true)
              : t(translations.entities.edit.seniors.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleUpdateCurrentSearch,
    handleApplyFilters,
    handleUpdateItem,
  } = usePaginatedTable<AffiliatedUser>({
    fetchData: handleGetSeniors,
  });

  const handleAskUpdateSeniorState = (seniorId: string, value: boolean) => {
    // Get the senior
    const senior = data.find((obj) => obj.id === seniorId)?.item.user;

    if (senior) {
      // Set the senior info for later
      dataToUpdateRef.current = {
        senior: senior,
        seniorActiveFlag: value,
      };

      // Open modal
      setUpdateSeniorDialogOpened({
        disableDialogOpened: !value,
        enableDialogOpened: value,
      });
    }
  };

  const handleDeclineUpdateSeniorState = () => {
    // Close modal
    setUpdateSeniorDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Clear
    dataToUpdateRef.current = undefined;
  };

  const handleConfirmUpdateSeniorState = () => {
    // Close modal
    setUpdateSeniorDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Update
    handleUpdateSeniorState();
  };

  const handleUpdateSeniorState = () => {
    if (dataToUpdateRef.current && dataToUpdateRef.current.senior.id) {
      // Keep values
      const senior = dataToUpdateRef.current.senior;
      const value = dataToUpdateRef.current.seniorActiveFlag;

      // Clear
      dataToUpdateRef.current = undefined;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === senior.id)?.item;
      if (updatedItem) {
        updatedItem.user.isActive = value;
      }

      // Update item in UI
      handleUpdateItem(
        senior.id as string,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: value
              ? t(translations.user_applications.state_value_true)
              : t(translations.user_applications.state_value_false),
          },
        ],
        updatedItem,
        value
      );

      // Make request to the backend
      // Note: Don't await because it will take too much time to update
      continueUpdateSeniorState(
        senior.id as string,
        senior.type,
        senior.product,
        senior.name,
        value
      );
    } else {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.entities.edit.seniors.errors.update_state_senior_error_message),
      });

      // Not expected. Log.
      console.debug(
        '🔴 Could not update the state of the senior. Information was missing to perform the request.'
      );
    }
  };

  const continueUpdateSeniorState = async (
    seniorId: string,
    seniorType: UserType,
    seniorProduct: Product,
    seniorName: string,
    value: boolean
  ) => {
    try {
      // Make request to the backend
      await UsersRepository.instance.updateById(seniorId, {
        product: seniorProduct,
        name: seniorName,
        type: seniorType,
        isActive: value,
      });
    } catch (error) {
      // Old flag
      const oldFlag: boolean = !value;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === seniorId)?.item;
      if (updatedItem) {
        updatedItem.user.isActive = oldFlag;
      }

      // Change item back
      handleUpdateItem(
        seniorId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: oldFlag
              ? t(translations.user_applications.state_value_true)
              : t(translations.user_applications.state_value_false),
          },
        ],
        updatedItem,
        oldFlag
      );

      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: value
          ? t(translations.entities.edit.seniors.errors.enable_senior_error_message)
          : t(translations.entities.edit.seniors.errors.disable_senior_error_message),
      });

      // Log
      console.debug(
        `🔴 Could not update the state of the senior with id ${seniorId} to ${value}.`,
        error
      );
    }
  };

  return (
    <ContentPage>
      <PageHeader
        showSearch={true}
        searchLabel={t(translations.entities.edit.seniors.search_input_placeholder)}
        searchText={currentSearch}
        searchOnChange={(text: string) => {
          // Update text search
          handleUpdateCurrentSearch(text);
        }}
        searchOnSubmit={() => {
          // Query backend about this
          handleApplyFilters(1, currentSearch);
        }}
      />
      <PaginatedTable
        avatar={{
          type: 'user',
        }}
        emptyTableMessage={t(translations.entities.edit.seniors.no_seniors)}
        columns={[
          {
            columnId: ColumnIdentifier.Name,
            title: t(translations.entities.edit.seniors.name_header),
            flexGrow: 2,
          },
          {
            columnId: ColumnIdentifier.Gender,
            title: t(translations.entities.edit.seniors.gender_header),
            flexGrow: 1,
          },
          {
            columnId: ColumnIdentifier.State,
            title: t(translations.entities.edit.seniors.state_header),
            flexGrow: 1,
            button: {
              type: 'toggle',
              showColumnValue: true,
              toggle: {
                enabledValue: t(translations.entities.edit.seniors.state_value_true),
                handleClick: handleAskUpdateSeniorState,
              },
            },
          },
        ]}
        rows={data}
        isLoading={isLoading}
        error={error}
        pagesCount={pagesCount}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        linkableRows={true}
      />
      <AlertModal
        id="confirm-disable-senior-state"
        opened={updateSeniorDialogOpened.disableDialogOpened}
        title={t(translations.entities.edit.seniors.disable_senior_dialog_title, {
          name: dataToUpdateRef.current?.senior.name || '',
        })}
        content={t(translations.entities.edit.seniors.disable_senior_dialog_message, {
          brand: getBrandName(appContext.state.configuration.product),
        })}
        handleClose={handleDeclineUpdateSeniorState}
        actions={[
          {
            label: t(translations.entities.edit.seniors.disable_senior_dialog_action_yes_button),
            type: 'destructive',
            handleClick: handleConfirmUpdateSeniorState,
          },
          {
            label: t(translations.entities.edit.seniors.enable_senior_dialog_action_no_button),
            type: 'neutral',
            handleClick: handleDeclineUpdateSeniorState,
          },
        ]}
      />
      <AlertModal
        id="confirm-enable-senior-state"
        opened={updateSeniorDialogOpened.enableDialogOpened}
        title={t(translations.entities.edit.seniors.enable_senior_dialog_title, {
          name: dataToUpdateRef.current?.senior.name || '',
        })}
        content={t(translations.entities.edit.seniors.enable_senior_dialog_message, {
          brand: getBrandName(appContext.state.configuration.product),
        })}
        handleClose={handleDeclineUpdateSeniorState}
        actions={[
          {
            label: t(translations.entities.edit.seniors.enable_senior_dialog_action_no_button),
            type: 'neutral',
            handleClick: handleDeclineUpdateSeniorState,
          },
          {
            label: t(translations.entities.edit.seniors.enable_senior_dialog_action_yes_button),
            type: 'nondestructive',
            handleClick: handleConfirmUpdateSeniorState,
          },
        ]}
      />
    </ContentPage>
  );
}
